import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  fetchFreeTrials,
  createFreeTrial,
  updateFreeTrial,
  deleteFreeTrial,
  readApiErrorMessage,
} from "../../services/apiClient";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  describeTrialPeriod,
  buildSubscriberPrefill,
  statusTone,
  statusLabel,
} from "./freeTrialViewModel";
import "./AdminUsers.css";

/* Free trials are administrative records: a prospect an admin is tracking by
   hand. Creating one does not touch a Clerk account, does not grant access to
   anything, and is unrelated to the automatic trial the users collection used
   to carry.

   Body cells carry a `data-label` matching their column heading: inert on a
   desktop, but below 760px AdminUsers.css turns each row into a card and
   renders the label beside the value. */

const EMPTY_FORM = {
  name: "",
  email: "",
  phone_number: "",
  start_date: "",
  expiry_date: "",
  notes: "",
};

function AdminFreeTrials() {
  const navigate = useNavigate();

  const [freeTrials, setFreeTrials] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.status) params.status = filters.status;
      const data = await fetchFreeTrials(params);
      setFreeTrials(data.free_trials || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (loadError) {
      setError(readApiErrorMessage(loadError, "Could not load free trials."));
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  // The period the two date fields describe right now. The server validates
  // the same rule; this is what lets the admin see the length before saving.
  const period = describeTrialPeriod(form.start_date, form.expiry_date);

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function save(event) {
    event.preventDefault();
    if (period && !period.valid) return;

    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (editingId) {
        await updateFreeTrial(editingId, form);
        setNotice("Free trial updated.");
      } else {
        await createFreeTrial(form);
        setNotice("Free trial added.");
      }
      resetForm();
      await load();
    } catch (saveError) {
      setError(readApiErrorMessage(saveError, "The free trial could not be saved."));
    } finally {
      setIsSaving(false);
    }
  }

  function edit(freeTrial) {
    setEditingId(freeTrial.id);
    setForm({
      name: freeTrial.name,
      email: freeTrial.email,
      phone_number: freeTrial.phone_number || "",
      start_date: freeTrial.start_date,
      expiry_date: freeTrial.expiry_date,
      notes: freeTrial.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /**
   * Hands the trial to the ordinary Subscribers page.
   *
   * No subscriber is created here. The admin lands on the normal Subscriber
   * form, pre-filled with what the trial knows, and fills in everything the
   * subscriber system requires — referrer, amount, subscription dates. That
   * form submits the conversion, and only a successful subscriber creation
   * marks this trial converted.
   */
  function convert(freeTrial) {
    navigate("/admin/subscribers", { state: { convertFreeTrial: buildSubscriberPrefill(freeTrial) } });
  }

  async function confirmDelete() {
    const request = deleteRequest;
    setDeleteRequest(null);
    if (!request) return;
    try {
      await deleteFreeTrial(request.id);
      if (editingId === request.id) resetForm();
      setNotice(`${request.name} was deleted.`);
      setError(null);
      await load();
    } catch (deleteError) {
      setError(readApiErrorMessage(deleteError, "The free trial could not be deleted."));
    }
  }

  return (
    <main className="admin-page">
      <section className="admin-workspace">
        <header className="admin-topbar">
          <div>
            <span className="admin-kicker">Administration</span>
            <strong>Free Trials</strong>
          </div>
          <span className="admin-count">
            {total} {total === 1 ? "free trial" : "free trials"}
          </span>
        </header>

        <div className="admin-content">
          <nav className="admin-tabs" aria-label="Administration sections">
            <Link to="/admin/users">Users</Link>
            <Link to="/admin/subscribers">Subscribers</Link>
            <Link className="is-active" to="/admin/free-trials">
              Free Trials
            </Link>
          </nav>

          <form className="admin-card admin-add" onSubmit={save}>
            <h2>{editingId ? "Edit free trial" : "Add free trial"}</h2>
            <p className="admin-hint admin-hint--block">
              A free trial is a record you keep by hand. Choose both dates yourself — there is no
              fixed length. Adding one does not create an account or grant any access.
            </p>

            <div className="admin-add__grid admin-add__grid--free-trial">
              <Field label="Name">
                <input
                  required
                  value={form.name}
                  onChange={(event) => change("name", event.target.value)}
                />
              </Field>
              <Field label="Email (optional)">
                {/* Not required, but `type="email"` still makes the browser
                    check the format of anything actually typed — the same rule
                    the server applies. */}
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => change("email", event.target.value)}
                />
              </Field>
              <Field label="Phone number (optional)">
                <input
                  type="tel"
                  value={form.phone_number}
                  onChange={(event) => change("phone_number", event.target.value)}
                />
              </Field>
              <Field label="Start date">
                <input
                  required
                  type="date"
                  value={form.start_date}
                  onChange={(event) => change("start_date", event.target.value)}
                />
              </Field>
              <Field label="Expiry date">
                <input
                  required
                  type="date"
                  value={form.expiry_date}
                  onChange={(event) => change("expiry_date", event.target.value)}
                />
              </Field>
              <Field label="Notes (optional)" wide>
                <textarea
                  rows="3"
                  value={form.notes}
                  onChange={(event) => change("notes", event.target.value)}
                />
              </Field>
            </div>

            {period ? (
              <p
                className={`admin-trial-period${period.valid ? "" : " is-invalid"}`}
                role="status"
              >
                {period.valid ? "Trial length: " : ""}
                {period.message}
              </p>
            ) : null}

            <div className="admin-actions">
              <button
                className="admin-btn admin-btn--primary"
                disabled={isSaving || Boolean(period && !period.valid)}
                type="submit"
              >
                {isSaving ? "Saving…" : editingId ? "Save changes" : "Add free trial"}
              </button>
              {editingId ? (
                <button className="admin-btn" type="button" onClick={resetForm}>
                  Cancel
                </button>
              ) : null}
            </div>
          </form>

          {notice ? <p className="admin-banner is-ok">{notice}</p> : null}
          {error ? <p className="admin-banner is-error">{error}</p> : null}

          <div className="admin-card">
            <div className="admin-filters admin-filters--subscribers">
              <input
                type="search"
                placeholder="Search name, email or phone"
                aria-label="Search free trials"
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
              />
              <select
                aria-label="Filter free trial status"
                value={filters.status}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="CONVERTED">Converted</option>
              </select>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table admin-table--free-trials">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Start Date</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} className="admin-empty">
                        Loading…
                      </td>
                    </tr>
                  ) : freeTrials.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="admin-empty">
                        No free trials match these filters.
                      </td>
                    </tr>
                  ) : (
                    freeTrials.map((freeTrial) => (
                      <FreeTrialRow
                        key={freeTrial.id}
                        freeTrial={freeTrial}
                        onEdit={edit}
                        onConvert={convert}
                        onDelete={setDeleteRequest}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <ConfirmDialog
        request={
          deleteRequest
            ? {
                title: "Delete free trial",
                body: `Delete ${deleteRequest.name}? This cannot be undone.`,
                confirmLabel: "Delete",
                tone: "danger",
              }
            : null
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteRequest(null)}
      />
    </main>
  );
}

function FreeTrialRow({ freeTrial, onEdit, onConvert, onDelete }) {
  const isConverted = freeTrial.status === "CONVERTED";

  return (
    <tr>
      <td data-label="Name">
        <div className="admin-user">
          <strong>{freeTrial.name}</strong>
          {freeTrial.notes ? <span title={freeTrial.notes}>Has notes</span> : null}
        </div>
      </td>
      <td data-label="Email">{freeTrial.email || "—"}</td>
      <td data-label="Phone">{freeTrial.phone_number || "—"}</td>
      <td data-label="Start Date">{freeTrial.start_date}</td>
      <td data-label="Expiry Date">{freeTrial.expiry_date}</td>
      <td data-label="Status">
        <span className={`admin-badge is-${statusTone(freeTrial.status)}`}>
          {statusLabel(freeTrial.status)}
        </span>
      </td>
      <td data-label="Actions">
        <div className="admin-actions">
          <button
            className="admin-btn"
            type="button"
            disabled={isConverted}
            title={isConverted ? "A converted free trial cannot be edited." : undefined}
            onClick={() => onEdit(freeTrial)}
          >
            Edit
          </button>
          <button
            className="admin-btn admin-btn--primary"
            type="button"
            disabled={isConverted}
            title={isConverted ? "Already converted to a subscriber." : undefined}
            onClick={() => onConvert(freeTrial)}
          >
            {isConverted ? "Converted" : "Convert to Subscriber"}
          </button>
          <button
            className="admin-btn admin-btn--danger"
            type="button"
            onClick={() => onDelete({ id: freeTrial.id, name: freeTrial.name })}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function Field({ label, wide, children }) {
  return (
    <label className={wide ? "admin-field--wide" : undefined}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export default AdminFreeTrials;
